/**
 * Sharpness — how much detail a photograph holds, as one number.
 * Pure module: no imports, no I/O.
 *
 * Variance of the Laplacian. A sharp edge produces a large second derivative;
 * blur flattens it. Take the variance of that response across the frame and you
 * have a single figure that ranks near-identical frames reliably.
 *
 * Deliberately arithmetic rather than a model: no download, no GPU, and — the
 * part that matters for trust — the same photograph always scores the same.
 * A neural scorer that changes its mind between runs is impossible to believe.
 *
 * The figure is meaningful only *relative to other frames measured the same way*,
 * so m3cull takes it twice, at two sizes, for two different questions.
 */

/**
 * The size at which focus is judged, and the tag that versions it.
 *
 * Sharpness is screened on the 240px filmstrip thumbnail, which is free — it
 * already exists — and instant. But a downscale is itself a low-pass filter, and
 * at 240px it removes most of what this measure exists to detect. Measured over
 * the fourteen bundled photographs, a frame and a blurred copy of it separate by:
 *
 *   240px    1.09×      the shipped screening measure
 *   640px    1.36×
 *   1024px   1.93×
 *   1600px   3.43×
 *
 * On a real 16 MP shoot of 138 frames, 1024px chooses a *different* sharpest
 * frame than 240px in 47% of five-frame bursts — so roughly half of every badge
 * placed on that shoot was placed on the wrong photograph.
 *
 * 1024 rather than 1600 or native because grain is high-frequency energy and so
 * is this measure. Against a clean sharp frame and a soft grainy one, ranked
 * correctly: 240px 8/14, 1024px 11/14, native 512px windows 9/14. The downscale
 * to 1024 is itself a denoiser, and going further up the resolution ladder trades
 * blur sensitivity for grain sensitivity. A low-pass before the Laplacian was
 * tried and rejected: it cost more signal (48% → 29%) than it bought.
 *
 * Change the measure, change the tag, and every shoot re-measures itself on its
 * next review without a model download or a schema change.
 */
export const FOCUS_PX = 1024;
export const FOCUS_TAG = 'lap-whole@1024';

export interface Grayscale {
  /** One byte per pixel, row-major. */
  data: Uint8Array;
  width: number;
  height: number;
}

/**
 * Variance of the Laplacian over the whole frame.
 * Returns 0 for an image too small to have interior pixels.
 */
export function laplacianVariance(image: Grayscale): number {
  const { data, width, height } = image;
  if (width < 3 || height < 3) return 0;

  // Welford's method: one pass, and no risk of catastrophic cancellation from
  // the naive sum-of-squares formula on frames with a large mean response.
  let n = 0;
  let mean = 0;
  let m2 = 0;

  for (let y = 1; y < height - 1; y++) {
    const row = y * width;
    const up = row - width;
    const down = row + width;

    for (let x = 1; x < width - 1; x++) {
      // 4-neighbour Laplacian: centre × -4 plus its orthogonal neighbours.
      const value =
        data[up + x] + data[down + x] + data[row + x - 1] + data[row + x + 1] - 4 * data[row + x];

      n++;
      const delta = value - mean;
      mean += delta / n;
      m2 += delta * (value - mean);
    }
  }

  return n > 1 ? m2 / (n - 1) : 0;
}

/** Rec. 709 luma, the weighting the eye actually uses. */
export function toGrayscale(rgba: Uint8ClampedArray | Uint8Array, width: number, height: number): Grayscale {
  const data = new Uint8Array(width * height);
  for (let i = 0, p = 0; p < data.length; i += 4, p++) {
    data[p] = (rgba[i] * 0.2126 + rgba[i + 1] * 0.7152 + rgba[i + 2] * 0.0722) | 0;
  }
  return { data, width, height };
}
